import React, { useState, useMemo, useCallback } from 'react';
import {
  Container,
  GridLegacy as Grid,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Alert,
  FormControl,
  Select,
  MenuItem,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  SelectChangeEvent,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  People as PeopleIcon,
  Phone as PhoneIcon,
  Timeline as TimelineIcon,
  AccessTime as AccessTimeIcon,
  Speed as SpeedIcon,
  EmojiEvents as TrophyIcon,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { Bar, Line } from 'react-chartjs-2';
import { format, subDays, subWeeks, subMonths, startOfWeek, endOfWeek, startOfMonth, endOfMonth } from 'date-fns';
import api from '../services/api';
import '../utils/chartSetup'; // Initialize Chart.js

// Types
interface RawAgentRecord {
  Date?: string;
  Agent?: string;
  agent?: string;
  Calls?: string | number;
  calls?: string | number;
  Contacts?: string | number;
  contacts?: string | number;
  'Talk %'?: string | number;
  'talk %'?: string | number;
  'Talk Time'?: string | number;
  'talk time'?: string | number;
  'ACW %'?: string | number;
  'acw %'?: string | number;
  ACW?: string | number;
  acw?: string | number;
  AHT?: string | number;
  aht?: string | number;
  'Avg Handle Time'?: string | number;
  'avg handle time'?: string | number;
  Utilization?: string | number;
  utilization?: string | number;
  'Utilization %'?: string | number;
  Sales?: string | number;
  sales?: string | number;
  SPH?: string | number;
  sph?: string | number;
  'Win Rate'?: string | number;
  'win rate'?: string | number;
  'Login Time'?: string;
  'login time'?: string;
}

interface AgentDataResponse {
  data?: RawAgentRecord[];
  totalRecords?: number;
}

interface AgentMetricsResponse {
  lastUpdated?: string;
}

interface FilteredMetrics {
  totalAgents: number;
  totalCalls: number;
  totalContacts: number;
  avgTalkPercent: string | number;
  avgACWPercent: string | number;
  avgAHT: string | number;
  avgUtilization: string | number;
  totalSales: number;
  avgSPH: string | number;
  avgWinRate: string | number;
}

interface AggregatedAgent {
  Agent: string;
  Calls: number;
  Contacts: number;
  'Login Time': string;
  'Talk %': string;
  'ACW %': string;
  AHT: string;
  Utilization: string;
  Sales: number;
  'Win Rate': string;
}

interface TopPerformer {
  name: string;
  sales: number;
}

interface TrendData {
  date: string;
  sales: number;
  calls: number;
}

type DateFilter = 'today' | 'previousDay' | 'currentWeek' | 'previousWeek' | 'currentMonth' | 'previousMonth' | 'dateRange' | 'all';
type SortOrder = 'asc' | 'desc';

const AgentEfficiency: React.FC = () => {
  const [dateFilter, setDateFilter] = useState<DateFilter>('currentWeek');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [orderBy, setOrderBy] = useState<keyof AggregatedAgent>('Sales');
  const [order, setOrder] = useState<SortOrder>('desc');

  const {
    data: agentData,
    isLoading: dataLoading,
    error: dataError,
  } = useQuery({
    queryKey: ['agentEfficiencyData'],
    queryFn: api.getAgentEfficiencyData,
    refetchInterval: 5 * 60 * 1000, // Refetch every 5 minutes
  });

  const {
    data: metricsData,
    isLoading: metricsLoading,
    error: metricsError,
  } = useQuery({
    queryKey: ['agentEfficiencyMetrics'],
    queryFn: api.getAgentEfficiencyMetrics,
    refetchInterval: 5 * 60 * 1000,
  });

  // Extract data safely
  const agentInfo: AgentDataResponse = agentData?.data || {};
  const rawData: RawAgentRecord[] = agentInfo.data || [];
  const metrics: AgentMetricsResponse = metricsData?.metrics || {};

  // Helper function to parse date from Google Sheets format and normalize to YYYY-MM-DD
  const parseSheetDate = useCallback((dateString?: string): Date | null => {
    if (!dateString) return null;
    
    try {
      // For YYYY-MM-DD format, append time to force local timezone interpretation
      let dateStr = dateString;
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
        dateStr = dateString + 'T00:00:00';
      }
      
      const parsedDate = new Date(dateStr);
      
      if (parsedDate && !isNaN(parsedDate.getTime())) {
        const currentYear = new Date().getFullYear();
        const parsedYear = parsedDate.getFullYear();
        
        // Allow dates within reasonable range
        if (parsedYear >= 2020 && parsedYear <= currentYear + 1) {
          return parsedDate;
        }
      }
      
      return null;
    } catch (e) {
      console.warn('Failed to parse date:', dateString, e);
      return null;
    }
  }, []);

  // Helper to normalize date to YYYY-MM-DD string for comparison
  const normalizeDate = useCallback((date?: Date | string | null): string | null => {
    if (!date) return null;
    const d = typeof date === 'string' ? new Date(date) : date;
    if (isNaN(d.getTime())) return null;
    
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }, []);

  // Filter data based on date selection
  const filteredData = useMemo((): RawAgentRecord[] => {
    if (dateFilter === 'all') return rawData;
    
    const now = new Date();
    
    return rawData.filter(record => {
      if (!record.Date) return false;
      
      const recordDate = parseSheetDate(record.Date);
      if (!recordDate || isNaN(recordDate.getTime())) return false;
      
      const normalizedRecordDate = normalizeDate(recordDate);
      
      switch (dateFilter) {
        case 'today':
          const todayNorm = normalizeDate(now);
          return normalizedRecordDate === todayNorm;
          
        case 'previousDay':
          const yesterdayNorm = normalizeDate(subDays(now, 1));
          return normalizedRecordDate === yesterdayNorm;
          
        case 'currentWeek':
          // Current week: Monday to Sunday of current week
          const weekStart = startOfWeek(now, { weekStartsOn: 1 }); // Monday
          const weekEnd = endOfWeek(now, { weekStartsOn: 1 });
          const weekStartNorm = normalizeDate(weekStart);
          const weekEndNorm = normalizeDate(weekEnd);
          return normalizedRecordDate! >= weekStartNorm! && normalizedRecordDate! <= weekEndNorm!;
          
        case 'previousWeek':
          // Previous week: Monday to Sunday of the week before current week
          const currentWeekStart = startOfWeek(now, { weekStartsOn: 1 });
          const prevWeekStart = subWeeks(currentWeekStart, 1);
          const prevWeekEnd = subDays(currentWeekStart, 1);
          const prevWeekStartNorm = normalizeDate(prevWeekStart);
          const prevWeekEndNorm = normalizeDate(prevWeekEnd);
          return normalizedRecordDate! >= prevWeekStartNorm! && normalizedRecordDate! <= prevWeekEndNorm!;
          
        case 'currentMonth':
          const monthStart = startOfMonth(now);
          const monthEnd = endOfMonth(now);
          const monthStartNorm = normalizeDate(monthStart);
          const monthEndNorm = normalizeDate(monthEnd);
          return normalizedRecordDate! >= monthStartNorm! && normalizedRecordDate! <= monthEndNorm!;
          
        case 'previousMonth':
          const prevMonthStart = startOfMonth(subMonths(now, 1));
          const prevMonthEnd = endOfMonth(subMonths(now, 1));
          const prevMonthStartNorm = normalizeDate(prevMonthStart);
          const prevMonthEndNorm = normalizeDate(prevMonthEnd);
          return normalizedRecordDate! >= prevMonthStartNorm! && normalizedRecordDate! <= prevMonthEndNorm!;
          
        case 'dateRange':
          if (!startDate || !endDate) return false;
          // Parse dates in local timezone to avoid UTC conversion issues
          const [startYear, startMonth, startDay] = startDate.split('-').map(Number);
          const [endYear, endMonth, endDay] = endDate.split('-').map(Number);
          const rangeStart = new Date(startYear, startMonth - 1, startDay);
          const rangeEnd = new Date(endYear, endMonth - 1, endDay);
          const rangeStartNorm = normalizeDate(rangeStart);
          const rangeEndNorm = normalizeDate(rangeEnd);
          return normalizedRecordDate! >= rangeStartNorm! && normalizedRecordDate! <= rangeEndNorm!;
          
        default:
          return true;
      }
    });
  }, [rawData, dateFilter, startDate, endDate, parseSheetDate, normalizeDate]);

  // Calculate filtered metrics
  const filteredMetrics = useMemo((): FilteredMetrics => {
    if (filteredData.length === 0) {
      return {
        totalAgents: 0,
        totalCalls: 0,
        totalContacts: 0,
        avgTalkPercent: 0,
        avgACWPercent: 0,
        avgAHT: 0,
        avgUtilization: 0,
        totalSales: 0,
        avgSPH: 0,
        avgWinRate: 0,
      };
    }

    let totalCalls = 0;
    let totalContacts = 0;
    let totalTalkPercent = 0;
    let totalACWPercent = 0;
    let totalAHT = 0;
    let totalUtilization = 0;
    let totalSales = 0;
    let totalSPH = 0;
    let totalWinRate = 0;
    let validRecords = 0;
    const uniqueAgents = new Set<string>();

    filteredData.forEach(row => {
      uniqueAgents.add(row.Agent || row.agent || 'Unknown');
      const calls = parseFloat(String(row.Calls || row.calls || 0));
      const contacts = parseFloat(String(row.Contacts || row.contacts || 0));
      const talkPercent = parseFloat(String(row['Talk %'] || row['talk %'] || row['Talk Time'] || row['talk time'] || '0').replace('%', ''));
      const acwPercent = parseFloat(String(row['ACW %'] || row['acw %'] || row['ACW'] || row['acw'] || '0').replace('%', ''));
      const aht = parseFloat(String(row.AHT || row.aht || 0));
      const utilization = parseFloat(String(row.Utilization || row.utilization || '0').replace('%', ''));
      const sales = parseFloat(String(row.Sales || row.sales || 0));
      const sph = parseFloat(String(row.SPH || row.sph || 0));
      const winRate = parseFloat(String(row['Win Rate'] || row['win rate'] || '0').replace('%', ''));

      if (!isNaN(calls)) totalCalls += calls;
      if (!isNaN(contacts)) totalContacts += contacts;
      if (!isNaN(talkPercent)) {
        totalTalkPercent += talkPercent;
        validRecords++;
      }
      if (!isNaN(acwPercent)) totalACWPercent += acwPercent;
      if (!isNaN(aht)) totalAHT += aht;
      if (!isNaN(utilization)) totalUtilization += utilization;
      if (!isNaN(sales)) totalSales += sales;
      if (!isNaN(sph)) totalSPH += sph;
      if (!isNaN(winRate)) totalWinRate += winRate;
    });

    return {
      totalAgents: uniqueAgents.size,
      totalCalls: Math.round(totalCalls),
      totalContacts: Math.round(totalContacts),
      avgTalkPercent: validRecords > 0 ? (totalTalkPercent / validRecords).toFixed(2) : 0,
      avgACWPercent: validRecords > 0 ? (totalACWPercent / validRecords).toFixed(2) : 0,
      avgAHT: validRecords > 0 ? (totalAHT / validRecords).toFixed(2) : 0,
      avgUtilization: validRecords > 0 ? (totalUtilization / validRecords).toFixed(2) : 0,
      totalSales: Math.round(totalSales),
      avgSPH: validRecords > 0 ? (totalSPH / validRecords).toFixed(2) : 0,
      avgWinRate: validRecords > 0 ? (totalWinRate / validRecords).toFixed(2) : 0,
    };
  }, [filteredData]);

  // Prepare chart data for top performers
  const topPerformersData = useMemo((): TopPerformer[] => {
    const agentStats: { [key: string]: TopPerformer } = {};
    
    filteredData.forEach(row => {
      const agent = row.Agent || row.agent || 'Unknown';
      const sales = parseFloat(String(row.Sales || row.sales || 0));
      
      if (!agentStats[agent]) {
        agentStats[agent] = { name: agent, sales: 0 };
      }
      agentStats[agent].sales += sales;
    });

    return Object.values(agentStats)
      .sort((a, b) => b.sales - a.sales)
      .slice(0, 10);
  }, [filteredData]);

  // Prepare trend data
  const trendData = useMemo((): TrendData[] => {
    const dateStats: { [key: string]: TrendData } = {};
    
    filteredData.forEach(row => {
      const date = row.Date || '';
      const sales = parseFloat(String(row.Sales || row.sales || 0));
      const calls = parseFloat(String(row.Calls || row.calls || 0));
      
      if (!dateStats[date]) {
        dateStats[date] = { date, sales: 0, calls: 0 };
      }
      dateStats[date].sales += sales;
      dateStats[date].calls += calls;
    });

    return Object.values(dateStats).sort((a, b) => {
      const dateA = parseSheetDate(a.date);
      const dateB = parseSheetDate(b.date);
      if (!dateA || !dateB) return 0;
      return dateA.getTime() - dateB.getTime();
    });
  }, [filteredData, parseSheetDate]);

  // Aggregate data by agent for the table
  const aggregatedData = useMemo((): AggregatedAgent[] => {
    const agentMap: { [key: string]: any } = {};
    
    filteredData.forEach(row => {
      const agent = row.Agent || row.agent || 'Unknown';
      
      if (!agentMap[agent]) {
        agentMap[agent] = {
          Agent: agent,
          Calls: 0,
          Contacts: 0,
          'Login Time': '',
          'Talk %': 0,
          'ACW %': 0,
          AHT: 0,
          Utilization: 0,
          Sales: 0,
          'Win Rate': 0,
          count: 0,
          totalLoginMinutes: 0,
        };
      }
      
      const agentData = agentMap[agent];
      agentData.Calls += parseFloat(String(row.Calls || row.calls || 0));
      agentData.Contacts += parseFloat(String(row.Contacts || row.contacts || 0));
      agentData['Talk %'] += parseFloat(String(row['Talk %'] || row['talk %'] || row['Talk Time'] || row['talk time'] || '0').replace('%', ''));
      agentData['ACW %'] += parseFloat(String(row['ACW %'] || row['acw %'] || row['ACW'] || row['acw'] || '0').replace('%', ''));
      agentData.AHT += parseFloat(String(row.AHT || row.aht || row['Avg Handle Time'] || row['avg handle time'] || 0));
      agentData.Utilization += parseFloat(String(row.Utilization || row.utilization || row['Utilization %'] || '0').replace('%', ''));
      agentData.Sales += parseFloat(String(row.Sales || row.sales || 0));
      agentData['Win Rate'] += parseFloat(String(row['Win Rate'] || row['win rate'] || '0').replace('%', ''));
      agentData.count++;
      
      // Parse login time (format: "H:MM:SS")
      const loginTime = row['Login Time'] || row['login time'] || '0:00:00';
      const timeParts = loginTime.split(':');
      if (timeParts.length >= 2) {
        const hours = parseInt(timeParts[0]) || 0;
        const minutes = parseInt(timeParts[1]) || 0;
        agentData.totalLoginMinutes += (hours * 60) + minutes;
      }
    });
    
    // Calculate averages and format
    return Object.values(agentMap).map((agent: any) => ({
      Agent: agent.Agent,
      Calls: Math.round(agent.Calls),
      Contacts: Math.round(agent.Contacts),
      'Login Time': `${Math.floor(agent.totalLoginMinutes / 60)}:${(agent.totalLoginMinutes % 60).toString().padStart(2, '0')}`,
      'Talk %': agent.count > 0 ? `${(agent['Talk %'] / agent.count).toFixed(2)}%` : '0%',
      'ACW %': agent.count > 0 ? `${(agent['ACW %'] / agent.count).toFixed(2)}%` : '0%',
      AHT: agent.count > 0 ? (agent.AHT / agent.count).toFixed(2) : '0.00',
      Utilization: agent.count > 0 ? `${(agent.Utilization / agent.count).toFixed(2)}%` : '0%',
      Sales: Math.round(agent.Sales),
      'Win Rate': agent.count > 0 ? `${(agent['Win Rate'] / agent.count).toFixed(2)}%` : '0%',
    }));
  }, [filteredData]);

  // Sorting and pagination logic
  const handleSort = useCallback((property: keyof AggregatedAgent) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  }, [orderBy, order]);

  const sortedData = useMemo((): AggregatedAgent[] => {
    return [...aggregatedData].sort((a, b) => {
      let aValue: any = a[orderBy];
      let bValue: any = b[orderBy];
      
      // Handle percentage strings
      if (typeof aValue === 'string' && aValue.includes('%')) {
        aValue = parseFloat(aValue.replace('%', ''));
      }
      if (typeof bValue === 'string' && bValue.includes('%')) {
        bValue = parseFloat(bValue.replace('%', ''));
      }
      
      // Convert to numbers if possible
      aValue = isNaN(parseFloat(String(aValue))) ? aValue : parseFloat(String(aValue));
      bValue = isNaN(parseFloat(String(bValue))) ? bValue : parseFloat(String(bValue));
      
      if (order === 'asc') {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      }
    });
  }, [aggregatedData, orderBy, order]);

  const paginatedData = useMemo((): AggregatedAgent[] => {
    return sortedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  }, [sortedData, page, rowsPerPage]);

  const formatNumber = useCallback((value: number): string => {
    return new Intl.NumberFormat('en-US').format(value);
  }, []);

  const handleDateFilterChange = useCallback((event: SelectChangeEvent<DateFilter>) => {
    setDateFilter(event.target.value as DateFilter);
    setPage(0);
  }, []);

  const handleChangePage = useCallback((event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    setPage(newPage);
  }, []);

  const handleChangeRowsPerPage = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  }, []);

  const getFilterLabel = useCallback((): string => {
    switch (dateFilter) {
      case 'today':
        return 'Today';
      case 'previousDay':
        return 'Previous Day';
      case 'currentWeek':
        return 'Current Week';
      case 'previousWeek':
        return 'Previous Week';
      case 'currentMonth':
        return 'Current Month';
      case 'previousMonth':
        return 'Previous Month';
      case 'dateRange':
        if (startDate && endDate) {
          const start = format(new Date(startDate), 'MMM dd, yyyy');
          const end = format(new Date(endDate), 'MMM dd, yyyy');
          return startDate === endDate ? start : `${start} - ${end}`;
        }
        return 'Date Range';
      case 'all':
        return 'All Time';
      default:
        return '';
    }
  }, [dateFilter, startDate, endDate]);

  if (dataLoading || metricsLoading) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress size={60} />
        </Box>
      </Container>
    );
  }

  if (dataError || metricsError) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">
          Failed to load agent efficiency data. Please check your connection and try again.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
        <Typography variant="h4">
          Agent Efficiency Analysis - Soniq CX
        </Typography>
        <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
          <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 180 } }}>
            <Select
              value={dateFilter}
              onChange={handleDateFilterChange}
              displayEmpty
              sx={{
                '& .MuiSelect-select': {
                  py: 1
                }
              }}
            >
              <MenuItem value="" disabled>
                <em>Select Time Period</em>
              </MenuItem>
              <MenuItem value="today">Current Day</MenuItem>
              <MenuItem value="previousDay">Previous Day</MenuItem>
              <MenuItem value="currentWeek">Current Week</MenuItem>
              <MenuItem value="previousWeek">Previous Week</MenuItem>
              <MenuItem value="currentMonth">Current Month</MenuItem>
              <MenuItem value="previousMonth">Previous Month</MenuItem>
              <MenuItem value="dateRange">Date Range</MenuItem>
              <MenuItem value="all">All Time</MenuItem>
            </Select>
          </FormControl>
          {dateFilter === 'dateRange' && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', width: { xs: '100%', sm: 'auto' } }}>
              <Typography variant="body2" sx={{ whiteSpace: 'nowrap', fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                From:
              </Typography>
              <TextField
                type="date"
                size="small"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                placeholder="Start date"
                sx={{ minWidth: { xs: '130px', sm: 150 } }}
                InputLabelProps={{
                  shrink: true,
                }}
                inputProps={{
                  max: endDate || undefined,
                  placeholder: 'Start date'
                }}
              />
              <Typography variant="body2" sx={{ whiteSpace: 'nowrap', fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                To:
              </Typography>
              <TextField
                type="date"
                size="small"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                placeholder="End date"
                sx={{ minWidth: { xs: '130px', sm: 150 } }}
                InputLabelProps={{
                  shrink: true,
                }}
                inputProps={{
                  min: startDate || undefined,
                  placeholder: 'End date'
                }}
              />
            </Box>
          )}
        </Box>
      </Box>

      {/* Key Metrics Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <PeopleIcon color="primary" sx={{ mr: 2, fontSize: 40 }} />
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Active Agents
                  </Typography>
                  <Typography variant="h5">
                    {formatNumber(filteredMetrics.totalAgents || 0)}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {getFilterLabel()}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <PhoneIcon color="primary" sx={{ mr: 2, fontSize: 40 }} />
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Total Calls
                  </Typography>
                  <Typography variant="h5">
                    {formatNumber(filteredMetrics.totalCalls || 0)}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {getFilterLabel()}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <PeopleIcon color="success" sx={{ mr: 2, fontSize: 40 }} />
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Total Contacts
                  </Typography>
                  <Typography variant="h5">
                    {formatNumber(filteredMetrics.totalContacts || 0)}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {getFilterLabel()}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <TrophyIcon color="primary" sx={{ mr: 2, fontSize: 40 }} />
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Total Sales
                  </Typography>
                  <Typography variant="h5">
                    {formatNumber(filteredMetrics.totalSales || 0)}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {getFilterLabel()}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <SpeedIcon color="primary" sx={{ mr: 2, fontSize: 40 }} />
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Avg Win Rate
                  </Typography>
                  <Typography variant="h5">
                    {filteredMetrics.avgWinRate || 0}%
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {getFilterLabel()}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <TimelineIcon color="primary" sx={{ mr: 2, fontSize: 40 }} />
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Avg Talk %
                  </Typography>
                  <Typography variant="h5">
                    {filteredMetrics.avgTalkPercent || 0}%
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {getFilterLabel()}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <AccessTimeIcon color="primary" sx={{ mr: 2, fontSize: 40 }} />
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Avg ACW %
                  </Typography>
                  <Typography variant="h5">
                    {filteredMetrics.avgACWPercent || 0}%
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {getFilterLabel()}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Top Performers Chart */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Top Performers by Sales
            </Typography>
            <Box height={300}>
              {topPerformersData.length > 0 ? (
                <Bar
                  data={{
                    labels: topPerformersData.map(p => p.name),
                    datasets: [
                      {
                        label: 'Sales',
                        data: topPerformersData.map(p => p.sales),
                        backgroundColor: 'rgba(54, 162, 235, 0.6)',
                        borderColor: 'rgba(54, 162, 235, 1)',
                        borderWidth: 1,
                      },
                    ],
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        display: false,
                      },
                      title: {
                        display: false,
                      },
                    },
                    scales: {
                      y: {
                        beginAtZero: true,
                        ticks: {
                          precision: 0,
                        },
                      },
                    },
                  }}
                />
              ) : (
                <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                  <Typography color="textSecondary">No data available</Typography>
                </Box>
              )}
            </Box>
          </Paper>
        </Grid>

        {/* Trend Chart */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Sales & Calls Trend
            </Typography>
            <Box height={300}>
              {trendData.length > 0 ? (
                <Line
                  data={{
                    labels: trendData.map(t => {
                      const date = parseSheetDate(t.date);
                      return date ? format(date, 'MMM dd') : t.date;
                    }),
                    datasets: [
                      {
                        label: 'Sales',
                        data: trendData.map(t => t.sales),
                        borderColor: 'rgba(75, 192, 192, 1)',
                        backgroundColor: 'rgba(75, 192, 192, 0.2)',
                        yAxisID: 'y',
                        tension: 0.1,
                      },
                      {
                        label: 'Calls',
                        data: trendData.map(t => t.calls),
                        borderColor: 'rgba(255, 99, 132, 1)',
                        backgroundColor: 'rgba(255, 99, 132, 0.2)',
                        yAxisID: 'y1',
                        tension: 0.1,
                      },
                    ],
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    interaction: {
                      mode: 'index' as const,
                      intersect: false,
                    },
                    plugins: {
                      legend: {
                        position: 'top' as const,
                      },
                    },
                    scales: {
                      y: {
                        type: 'linear' as const,
                        display: true,
                        position: 'left' as const,
                        title: {
                          display: true,
                          text: 'Sales',
                        },
                      },
                      y1: {
                        type: 'linear' as const,
                        display: true,
                        position: 'right' as const,
                        title: {
                          display: true,
                          text: 'Calls',
                        },
                        grid: {
                          drawOnChartArea: false,
                        },
                      },
                    },
                  }}
                />
              ) : (
                <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                  <Typography color="textSecondary">No data available</Typography>
                </Box>
              )}
            </Box>
          </Paper>
        </Grid>

        {/* Detailed Data Table */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Detailed Agent Data
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <TableSortLabel
                        active={orderBy === 'Agent'}
                        direction={orderBy === 'Agent' ? order : 'asc'}
                        onClick={() => handleSort('Agent')}
                      >
                        Agent
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="right">
                      <TableSortLabel
                        active={orderBy === 'Calls'}
                        direction={orderBy === 'Calls' ? order : 'asc'}
                        onClick={() => handleSort('Calls')}
                      >
                        Calls
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="right">
                      <TableSortLabel
                        active={orderBy === 'Contacts'}
                        direction={orderBy === 'Contacts' ? order : 'asc'}
                        onClick={() => handleSort('Contacts')}
                      >
                        Contacts
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="right">
                      <TableSortLabel
                        active={orderBy === 'Login Time'}
                        direction={orderBy === 'Login Time' ? order : 'asc'}
                        onClick={() => handleSort('Login Time')}
                      >
                        Login Time
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="right">
                      <TableSortLabel
                        active={orderBy === 'Talk %'}
                        direction={orderBy === 'Talk %' ? order : 'asc'}
                        onClick={() => handleSort('Talk %')}
                      >
                        Talk %
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="right">
                      <TableSortLabel
                        active={orderBy === 'ACW %'}
                        direction={orderBy === 'ACW %' ? order : 'asc'}
                        onClick={() => handleSort('ACW %')}
                      >
                        ACW %
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="right">
                      <TableSortLabel
                        active={orderBy === 'AHT'}
                        direction={orderBy === 'AHT' ? order : 'asc'}
                        onClick={() => handleSort('AHT')}
                      >
                        AHT
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="right">
                      <TableSortLabel
                        active={orderBy === 'Utilization'}
                        direction={orderBy === 'Utilization' ? order : 'asc'}
                        onClick={() => handleSort('Utilization')}
                      >
                        Utilization
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="right">
                      <TableSortLabel
                        active={orderBy === 'Sales'}
                        direction={orderBy === 'Sales' ? order : 'asc'}
                        onClick={() => handleSort('Sales')}
                      >
                        Sales
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="right">
                      <TableSortLabel
                        active={orderBy === 'Win Rate'}
                        direction={orderBy === 'Win Rate' ? order : 'asc'}
                        onClick={() => handleSort('Win Rate')}
                      >
                        Win Rate
                      </TableSortLabel>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedData.length > 0 ? (
                    paginatedData.map((row, index) => (
                      <TableRow key={index}>
                        <TableCell>{row.Agent || 'Unknown'}</TableCell>
                        <TableCell align="right">{row.Calls}</TableCell>
                        <TableCell align="right">{row.Contacts}</TableCell>
                        <TableCell align="right">{row['Login Time']}</TableCell>
                        <TableCell align="right">{row['Talk %']}</TableCell>
                        <TableCell align="right">{row['ACW %']}</TableCell>
                        <TableCell align="right">{row.AHT}</TableCell>
                        <TableCell align="right">{row.Utilization}</TableCell>
                        <TableCell align="right">{row.Sales}</TableCell>
                        <TableCell align="right">{row['Win Rate']}</TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={10} align="center">
                        <Typography color="textSecondary">No data available for the selected period</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50]}
              component="div"
              count={sortedData.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Paper>
        </Grid>

        {/* Data Summary */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Data Summary
            </Typography>
            <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
              <Chip
                label={`${agentInfo.totalRecords || 0} Soniq CX records`}
                color="primary"
                variant="outlined"
              />
              <Chip
                label={`${filteredData.length} records in view`}
                color="secondary"
                variant="outlined"
              />
              <Chip
                label={`Last updated: ${metrics.lastUpdated 
                  ? format(new Date(metrics.lastUpdated), 'MMM dd, yyyy HH:mm')
                  : 'Never'}`}
                color="info"
                variant="outlined"
              />
              <Typography variant="body2" color="textSecondary">
                Real-time sync with Google Sheets
              </Typography>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default AgentEfficiency;
